import { MarkdownContent } from "@/components/chat/MarkdownContent";
import {
  BEAD_STATE_DISPLAY_LABEL,
  BEAD_TYPE_DISPLAY_LABEL,
  type BeadValueObject,
} from "../../../domain/praxis-beads";
import { useBeads } from "../../hooks/useBeads";

interface DetailProps {
  readonly beadId: string;
}

function formatRelative(ms: number | undefined): string {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const abs = Math.abs(diff);
  const past = diff >= 0;
  const day = 86_400_000;
  const hour = 3_600_000;
  if (abs < hour) return past ? "just now" : "soon";
  if (abs < day) {
    const h = Math.round(abs / hour);
    return past ? `${h}h ago` : `in ${h}h`;
  }
  const d = Math.round(abs / day);
  return past ? `${d}d ago` : `in ${d}d`;
}

export function BeadCompactRow({ bead }: { readonly bead: BeadValueObject }) {
  return (
    <div className="flex items-center gap-2 border-b border-home-ink/8 px-3 py-2 dark:border-white/10">
      <span className="flex-1 truncate text-xs font-semibold text-home-ink/85 dark:text-white/85">
        {bead.fields.title}
      </span>
      <span className="rounded-full border border-home-ink/10 px-2 py-0.5 text-[11px] font-medium text-home-ink/50 dark:border-white/15 dark:text-white/50">
        {BEAD_TYPE_DISPLAY_LABEL[bead.type]}
      </span>
      <span className="rounded-full bg-home-ink/[0.05] px-2 py-0.5 text-[11px] font-medium text-home-ink/50 dark:bg-white/10 dark:text-white/50">
        {BEAD_STATE_DISPLAY_LABEL[bead.state]}
      </span>
    </div>
  );
}

export function BeadDetailDrawer({ beadId }: DetailProps) {
  const { beads, dependencies, labels } = useBeads();
  // biome-ignore lint/suspicious/noExplicitAny: convex Id at runtime is string
  const allBeads = beads as unknown as Array<BeadValueObject & { _id: string; userId: string }>;
  const bead = allBeads.find((b) => b._id === beadId);

  if (!bead) {
    return (
      <div className="flex h-full items-center justify-center px-8 text-center text-sm italic text-home-ink/50 dark:text-white/50">
        Bead not found.
      </div>
    );
  }

  // Resolved labels & dependencies for this bead.
  // biome-ignore lint/suspicious/noExplicitAny: convex rows
  const beadLabels = (labels as any[]).filter((l) => l.beadId === beadId).map(
    (l) => l.label as string,
  );
  // biome-ignore lint/suspicious/noExplicitAny: convex rows
  const outgoing = (dependencies as any[]).filter(
    (d) => d.sourceBeadId === beadId,
  );
  const resolvedDeps = outgoing.map((d) => {
    const target = allBeads.find((b) => b._id === d.targetBeadId);
    return {
      id: d._id as string,
      title: target?.fields.title ?? "(unknown)",
      relation: d.relationType as string,
    };
  });

  return (
    <div className="flex h-full flex-col overflow-y-auto p-4 text-sm text-home-ink/80 dark:text-white/80">
      <header className="mb-4">
        <h2 className="text-lg font-semibold text-home-ink dark:text-white">
          {bead.fields.title}
        </h2>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="rounded-full border border-home-ink/10 px-2 py-0.5 text-xs font-medium text-home-ink/55 dark:border-white/15 dark:text-white/60">
            {BEAD_TYPE_DISPLAY_LABEL[bead.type]}
          </span>
          <span className="rounded-full bg-home-ink/[0.05] px-2 py-0.5 text-xs font-medium text-home-ink/55 dark:bg-white/10 dark:text-white/60">
            {BEAD_STATE_DISPLAY_LABEL[bead.state]}
          </span>
          <span className="text-xs text-home-ink/40 dark:text-white/40">
            P{bead.priority} • {bead.schemaVersion}
          </span>
        </div>
        <div className="mt-1 text-xs text-home-ink/40 dark:text-white/40">
          Created {formatRelative(bead.metadata.createdAt)} • Updated{" "}
          {formatRelative(bead.metadata.updatedAt)}
        </div>
      </header>

      <PerTypeFields bead={bead} />

      {bead.fields.description ? (
        <Section label="Description">
          <div className="prose prose-sm dark:prose-invert max-w-none text-home-ink/85 dark:text-white/85">
            <MarkdownContent content={bead.fields.description} />
          </div>
        </Section>
      ) : null}

      {bead.fields.notes ? (
        <Section label="Notes">
          <div className="prose prose-sm dark:prose-invert max-w-none text-home-ink/85 dark:text-white/85">
            <MarkdownContent content={bead.fields.notes} />
          </div>
        </Section>
      ) : null}

      {beadLabels.length > 0 ? (
        <Section label="Labels">
          <div className="flex flex-wrap gap-1">
            {beadLabels.map((label) => (
              <span
                key={label}
                className="rounded-full border border-home-ink/10 px-2 py-0.5 text-xs font-medium text-home-ink/55 dark:border-white/15 dark:text-white/60"
              >
                {label}
              </span>
            ))}
          </div>
        </Section>
      ) : null}

      {resolvedDeps.length > 0 ? (
        <Section label="Dependencies">
          <ul className="flex flex-col gap-1">
            {resolvedDeps.map((d) => (
              <li
                key={d.id}
                className="rounded border border-home-ink/10 px-2 py-1 text-xs text-home-ink/70 dark:border-white/15 dark:text-white/70"
              >
                {d.relation} → {d.title}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {bead.extensions && Object.keys(bead.extensions).length > 0 ? (
        <Section label="Extensions">
          <pre className="overflow-x-auto rounded bg-home-ink/[0.05] p-2 text-xs dark:bg-white/10">
            {JSON.stringify(bead.extensions, null, 2)}
          </pre>
        </Section>
      ) : null}
    </div>
  );
}

function Section({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section className="mb-3">
      <h3 className="mb-1 text-xs font-bold  text-home-ink/40 dark:text-white/40">
        {label}
      </h3>
      {children}
    </section>
  );
}

function PerTypeFields({ bead }: { readonly bead: BeadValueObject }) {
  switch (bead.fields.kind) {
    case "project":
      return bead.fields.outcome ? (
        <Section label="Outcome">
          <p className="text-sm leading-relaxed">{bead.fields.outcome}</p>
        </Section>
      ) : null;
    case "waiting-for":
      return (
        <Section label="Waiting on">
          <p className="text-sm">
            <span className="font-semibold">{bead.fields.waiter}</span>
            {bead.fields.expectedAt ? (
              <>
                <span className="text-home-ink/40 dark:text-white/40"> · expected </span>
                {formatRelative(bead.fields.expectedAt)}
              </>
            ) : null}
          </p>
        </Section>
      );
    case "decision":
      return (
        <Section label="Decided">
          <p className="text-sm">{formatRelative(bead.fields.decidedAt)}</p>
        </Section>
      );
    case "commitment":
      return (
        <Section label="Commitment">
          <p className="text-sm">
            <span className="font-semibold">{bead.fields.committedTo}</span>
            <span className="text-home-ink/40 dark:text-white/40"> · due </span>
            {formatRelative(bead.fields.due)}
          </p>
        </Section>
      );
    case "output":
      return (
        <Section label="Output">
          <p className="text-sm">
            Spec: <span className="font-mono">{bead.fields.specId}</span>
            {bead.fields.ratifiedAt ? (
              <>
                <span className="text-home-ink/40 dark:text-white/40"> · ratified </span>
                {formatRelative(bead.fields.ratifiedAt)}
              </>
            ) : null}
          </p>
        </Section>
      );
    default:
      return null;
  }
}

